import { useCallback, useMemo, useRef } from "react";
import {
    type ImageCarConfigType,
    type ImageCarType,
    type ImageOptionsType,
    ImageMode,
} from "../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { useCarConfigSelector } from "../redux/store";
import { getCarImage, getCarImageCacheKey } from "../../shared-logic/utils/imageUtils";
import { useCarConfigCCISNormalization, useCarIds } from "./useAphCCISNormalization";
import useSpinner, { type UseSpinnerType } from "../../../../common-deprecated/hooks/spin/useSpinner";
import { getCarDBAssetUrl } from "../../../shared-logic/utils/imageUtils";
import { findPackExtraImage } from "../utils/packUtils";
import { useAphMobilityExtras } from "./useAphMobilityExtras";

export const useAphVisual = (
    frame: number,
    options: ImageOptionsType = {
        type: "webp",
    },
): string => {
    const carIds = useCarIds();
    const carImageInput = useCarConfigCCISNormalization();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carImage = getCarImage(commonSettings, frame, carIds, options, carImageInput).src;
    return carImage;
};

/**
 * A function that returns a function to get a specific car image source.
 *
 * @param {number} index - The index of the car image to retrieve.
 * @return {{src: string}} The source of the specific car image.
 */
export const useAphImageSrc = (
    options: ImageOptionsType = {
        type: "webp",
    },
): ((imageIndex: number) => { src: string; srcSet: string }) => {
    const carIds = useCarIds();
    const carImageInput = useCarConfigCCISNormalization();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const hasMobilitySolutions = useAphMobilityExtras().exteriorClaySupportedExtras.length > 0;
    const cacheKey = getCarImageCacheKey(carIds, carImageInput);
    const { useAvailableInOtherGradeImages } = options;

    return useCallback(
        (index: number): { src: string; srcSet: string } =>
            getCarImage(
                commonSettings,
                index,
                useAvailableInOtherGradeImages?.overrideCarIds ?? carIds,
                options,
                useAvailableInOtherGradeImages?.overrideCarImageInput ?? carImageInput,
                hasMobilitySolutions ? ImageMode.ClayExterior : ImageMode.Exterior,
            ),
        [cacheKey, carIds, useAvailableInOtherGradeImages],
    );
};

export const useSpin = (
    initialIndex: number = 0,
    imageOptions: ImageOptionsType = {},
    onSpin?: () => void,
): {
    frameProps: UseSpinnerType;
    cacheKey: string;
    getImageSrc: (index: number) => { src: string };
} => {
    const carIds = useCarIds();
    const carImageInput = useCarConfigCCISNormalization();
    const cacheKey = getCarImageCacheKey(carIds, carImageInput);
    const getImage = useAphImageSrc({ type: "webp", bgColour: "FFFFFF", ...imageOptions });

    const getImageSrc = (frame: number): { src: string; srcSet: string; sizes: string } => {
        return {
            ...getImage(frame),
            sizes: "", // TODO: sizes
        };
    };

    const frameProps = useSpinner(getImageSrc, cacheKey, initialIndex, undefined, onSpin);

    return {
        frameProps,
        cacheKey,
        getImageSrc,
    };
};

export type ImageCarConfigOverrideType = { imageCarConfig: ImageCarConfigType; carIds: ImageCarType };

export const useInteriorCarImages = (
    frames: number[] = [3, 1, 5, 4, 0, 2],
    enabled: boolean,
    imageOptions?: ImageOptionsType,
): { src: string; srcSet: string }[] => {
    const carIds = useCarIds();
    const carConfig = useCarConfigCCISNormalization();
    const hasMobilitySolutions = useAphMobilityExtras().interiorClaySupportedExtras.length > 0;
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    // Frame 0: Dashboard is not support in Clay Mode thus we need to filter this frame out
    const processedFrames = hasMobilitySolutions ? frames.filter((frame) => frame !== 0) : frames;
    const previousImages = useRef<{ src: string; srcSet: string }[] | null>(null);

    return useMemo(() => {
        if (!enabled && previousImages.current) {
            return previousImages.current;
        }

        const newImages = processedFrames.map((index) =>
            getCarImage(
                commonSettings,
                index,
                imageOptions?.useAvailableInOtherGradeImages?.overrideCarIds ?? carIds,
                { type: "webp", bgColour: "FFFFFF", width: 1280, height: 1280, imageQuality: 80, ...imageOptions },
                imageOptions?.useAvailableInOtherGradeImages?.overrideCarImageInput ?? carConfig,
                hasMobilitySolutions ? ImageMode.ClayInterior : ImageMode.Interior,
            ),
        );

        previousImages.current = newImages; // Store new images

        return newImages;
    }, [enabled, processedFrames, commonSettings, carIds, carConfig, hasMobilitySolutions]);
};

/**
 * @param {boolean} useFallback - When provided, the first found image out of all the items will be returned if the one from the basket can't be found.
 */
export const usePacksVisual = (useFallback: boolean = false): string => {
    const basketPacks = useCarConfigSelector((state) => state.basket.packs);
    const basketExtras = useCarConfigSelector((state) => state.basket.extras);
    const possiblePacks = useCarConfigSelector((state) => state.items.packs);
    const possibleExtras = useCarConfigSelector((state) => state.items.extras);
    const compressedCcisPath = useCarConfigSelector((state) => state.commonSettings.compressedCcisPath);

    const image = useMemo(() => {
        return findPackExtraImage(
            basketPacks,
            basketExtras,
            useFallback ? { possiblePacks, possibleExtras } : undefined,
        );
    }, [basketExtras, basketPacks, useFallback, possibleExtras, possiblePacks]);

    if (image) {
        const url = getCarDBAssetUrl(image, compressedCcisPath, 200, 200);
        return `var(--or-color-grey-1) url('${url}') center/cover no-repeat`;
    }
    return "var(--or-color-grey-1)";
};
